/** POS-80C / RP 260 IV — 80mm thermal paper (driver handles hardware margins). */
const PAPER_WIDTH_MM = 80;
const THERMAL_ROLL_LENGTH_MM = 210;

// Change padding here — top, left/right, bottom (e.g. '1mm 1mm 0', '2mm 3mm 1mm')
const RECEIPT_PADDING = '0 4mm 0';

/** Bills taller than this use default page margins + standard roll height. */
const LARGE_BILL_HEIGHT_MM = 170;
/** Approximates Chrome print dialog "Default" margins (~0.4in). */
const DEFAULT_PAGE_MARGIN_MM = 10;

const IFRAME_PRINT_CSS = `
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    width: ${PAPER_WIDTH_MM}mm;
    height: auto;
    min-height: 0;
    background: #fff;
  }

  body {
    display: block;
    box-sizing: border-box;
  }

  #pos-receipt-print {
    display: block;
    box-sizing: border-box;
    width: 100% !important;
    max-width: ${PAPER_WIDTH_MM}mm !important;
    margin: 0 !important;
    padding: ${RECEIPT_PADDING} !important;
    border: none !important;
    border-radius: 0 !important;
    background: #fff !important;
    color: #000 !important;
    font-family: 'IBM Plex Mono', 'Courier New', monospace;
    font-size: 10.5px;
    line-height: 1.25;
    box-shadow: none !important;
  }

  #pos-receipt-print *:not(img) {
    color: #000 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  #pos-receipt-print img {
    box-shadow: none !important;
  }

  #pos-receipt-print [class*="divider"] {
    margin: 4px 0 !important;
    border-top: 1px solid #000 !important;
  }

  #pos-receipt-print [class*="itemsHead"],
  #pos-receipt-print [class*="itemRow"] {
    grid-template-columns: 1fr 22px 54px !important;
    gap: 2px !important;
  }

  #pos-receipt-print [class*="itemsHead"] {
    margin-bottom: 2px !important;
  }

  #pos-receipt-print [class*="itemRow"] {
    padding: 1px 0 !important;
  }

  #pos-receipt-print [class*="lineItemRow"]:not(:last-child) {
    margin-bottom: 1em !important;
  }

  #pos-receipt-print [class*="sumRow"] {
    padding: 1px 0 !important;
  }

  #pos-receipt-print [class*="sumTotal"] {
    padding-top: 3px !important;
  }

  #pos-receipt-print [class*="itemSavingsText"] {
    font-weight: 600 !important;
  }

  #pos-receipt-print [class*="sumSavings"] {
    font-weight: 700 !important;
  }

  #pos-receipt-print [class*="savingsCallout"] {
    border-top: 1px dashed #000 !important;
    border-bottom: 1px dashed #000 !important;
    text-align: center !important;
    margin: 4px 0 !important;
    padding: 2px 0 !important;
    font-size: 10px !important;
    font-weight: 700 !important;
  }

  #pos-receipt-print [class*="receiptHead"] {
    text-align: center !important;
  }

  #pos-receipt-print [class*="revisedBanner"] {
    margin-top: 2mm !important;
    font-size: 12px !important;
    font-weight: 800 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    color: #000 !important;
  }

  #pos-receipt-print [class*="revisedRevision"] {
    margin-top: 0.5mm !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    color: #000 !important;
  }

  #pos-receipt-print [class*="billTitle"] {
    margin-top: 1.5mm !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    letter-spacing: 0.06em !important;
    text-transform: uppercase !important;
  }

  #pos-receipt-print [class*="storeLogo"] {
    display: block !important;
    width: min(100%, 58mm) !important;
    max-width: 58mm !important;
    height: auto !important;
    margin: 0 auto 2mm !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #pos-receipt-print [class*="billMeta"] {
    display: flex !important;
    flex-wrap: wrap !important;
    column-gap: 8px !important;
    row-gap: 2px !important;
    font-size: 9.5px !important;
    line-height: 1.3 !important;
  }

  #pos-receipt-print [class*="billMetaItem"] {
    flex: 1 1 calc(50% - 4px) !important;
    min-width: min(100%, 8rem) !important;
    white-space: nowrap !important;
  }

  #pos-receipt-print [class*="billMetaItem"]:nth-child(even) {
    text-align: right !important;
  }

  #pos-receipt-print [class*="billMetaWide"] {
    flex: 1 1 100% !important;
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
  }

  #pos-receipt-print [class*="feedbackNote"] {
    margin-top: 6px !important;
    font-size: 9.5px !important;
  }

  #pos-receipt-print [class*="thankYou"] {
    margin-top: 4px !important;
  }
`;

function buildPageRule(heightMm) {
  const isLargeBill = heightMm > LARGE_BILL_HEIGHT_MM;

  if (isLargeBill) {
    // Large bill → Chrome "Default" margins, paginate on standard roll length.
    return `@page { size: ${PAPER_WIDTH_MM}mm ${THERMAL_ROLL_LENGTH_MM}mm; margin: ${DEFAULT_PAGE_MARGIN_MM}mm; }`;
  }

  // Small bill → Chrome "None" margins, single page sized to content.
  return `@page { size: ${PAPER_WIDTH_MM}mm ${heightMm}mm; margin: 0 !important; }`;
}

function copyStylesInto(doc) {
  document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    doc.head.appendChild(node.cloneNode(true));
  });
}

function measurePageHeightMm(rootEl, widthMm) {
  const pxPerMm = rootEl.offsetWidth > 0 ? rootEl.offsetWidth / widthMm : 3.7795275591;
  if (rootEl.offsetHeight <= 0) return 120;
  return Math.max(40, Math.ceil(rootEl.offsetHeight / pxPerMm) + 2);
}

/**
 * Print a receipt element via an isolated iframe so app layout/CSS
 * (flex centering, height:100%) cannot affect thermal roll sizing.
 */
export function printReceiptBrowser(receiptEl) {
  if (!receiptEl) {
    window.print();
    return null;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Receipt print');
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  doc.open();
  doc.write('<!DOCTYPE html><html><head><meta charset="utf-8"></head><body></body></html>');
  doc.close();

  copyStylesInto(doc);

  const layoutStyle = doc.createElement('style');
  layoutStyle.textContent = IFRAME_PRINT_CSS;
  doc.head.appendChild(layoutStyle);

  const clone = receiptEl.cloneNode(true);
  clone.id = 'pos-receipt-print';
  doc.body.appendChild(clone);

  const finishPrint = () => {
    iframe.remove();
  };

  const runPrint = () => {
    const win = iframe.contentWindow;
    if (!iframe.isConnected || !win) return;

    const heightMm = measurePageHeightMm(clone, PAPER_WIDTH_MM);
    const pageStyle = doc.createElement('style');
    pageStyle.textContent = buildPageRule(heightMm);
    doc.head.appendChild(pageStyle);

    win.addEventListener('afterprint', finishPrint, { once: true });
    win.focus();
    win.print();
  };

  // Allow cloned stylesheets and fonts to apply before measuring.
  requestAnimationFrame(() => {
    requestAnimationFrame(runPrint);
  });

  return iframe;
}
